<aside class="entity-list" aria-label="Current observers and detected drones">
  <header class="entity-list__header">
    <div>
      <h2>Current activity</h2>
      <p>{{ observers().length }} observers · {{ drones().length }} drones</p>
    </div>

    <button class="entity-list__close" type="button" aria-label="Close activity list" (click)="facade.closeEntityList()">
      &times;
    </button>
  </header>

  <div class="entity-list__tabs" role="tablist" aria-label="Activity type">
    <button
      type="button"
      role="tab"
      [attr.aria-selected]="activeTab() === 'observers'"
      [class.entity-list__tab--active]="activeTab() === 'observers'"
      (click)="setActiveTab('observers')"
    >
      Observers
      <span>{{ observers().length }}</span>
    </button>

    <button
      type="button"
      role="tab"
      [attr.aria-selected]="activeTab() === 'drones'"
      [class.entity-list__tab--active]="activeTab() === 'drones'"
      (click)="setActiveTab('drones')"
    >
      Drones
      <span>{{ drones().length }}</span>
    </button>
  </div>

  @if (activeTab() === 'observers') {
    <section class="entity-section" aria-label="Observers">
      <div class="entity-section__header">
        <h3>Observers</h3>

        <label class="entity-sort">
          <span>Sort</span>
          <select [value]="observerSort()" (change)="updateObserverSort($event)" aria-label="Sort observers">
            <option value="observerId">Observer ID</option>
            <option value="receivedAtUtc">Newest</option>
            <option value="photo">Has photo</option>
          </select>
        </label>
      </div>

      @if (sortedObservers().length) {
        <div class="entity-list__cards">
          @for (item of sortedObservers(); track trackObserver($index, item)) {
            <article class="entity-card">
              <div class="entity-card__top">
                <div class="entity-card__media">
                  @if (item.photo; as photo) {
                    <button
                      class="entity-thumbnail"
                      type="button"
                      aria-label="Open observer photo"
                      (click)="facade.openObserverPhoto(photo, [photo])"
                    >
                      <img [src]="photo.imageUrl" alt="" />
                    </button>
                  } @else if (item.hasPhoto) {
                    <span class="entity-thumbnail entity-thumbnail--loading" aria-label="Photo loading"></span>
                  } @else {
                    <span class="entity-thumbnail entity-thumbnail--empty" aria-label="No photo"></span>
                  }
                </div>

                <div class="entity-card__identity">
                  <span class="entity-card__title">Observer {{ item.observer.observerId }}</span>
                  <span class="entity-card__subtitle">{{ item.observer.deviceId || 'Unknown device' }}</span>
                  <span class="entity-card__time">
                    {{ formatMetadataValue(item.observer, { label: 'Received', key: 'receivedAtUtc', type: 'date' }) }}
                  </span>
                </div>

                <button
                  class="entity-card__zoom"
                  type="button"
                  [attr.aria-label]="'Zoom to observer ' + item.observer.observerId"
                  [attr.title]="'Zoom to observer ' + item.observer.observerId"
                  (click)="viewport.zoomToObserver(item.observer)"
                ></button>
              </div>

              <details class="entity-card__details">
                <summary>Metadata</summary>
                <dl>
                  @for (field of observerMetadataFields; track field.key) {
                    <div>
                      <dt>{{ field.label }}</dt>
                      <dd>{{ formatMetadataValue(item.observer, field) }}</dd>
                    </div>
                  }
                </dl>
              </details>
            </article>
          }
        </div>
      } @else {
        <p class="entity-section__empty">No observers in the current response.</p>
      }
    </section>
  } @else {
    <section class="entity-section" aria-label="Detected drones">
      <div class="entity-section__header">
        <h3>Drones detected</h3>

        <label class="entity-sort">
          <span>Sort</span>
          <select [value]="droneSort()" (change)="updateDroneSort($event)" aria-label="Sort detected drones">
            <option value="lastSeenAtUtc">Newest</option>
            <option value="confidence">Confidence</option>
            <option value="observerCount">Observers</option>
          </select>
        </label>
      </div>

      @if (sortedDrones().length) {
        <div class="entity-list__cards">
          @for (item of sortedDrones(); track trackDrone($index, item)) {
            <article class="entity-card">
              <div class="entity-card__top">
                <div class="entity-card__media entity-card__media--stack">
                  @if (item.photos.length) {
                    @for (photo of item.photos; track trackPhoto($index, photo)) {
                      <button
                        class="entity-thumbnail entity-thumbnail--stacked"
                        type="button"
                        aria-label="Open related observer photo"
                        (click)="facade.openObserverPhoto(photo, item.photos)"
                      >
                        <img [src]="photo.imageUrl" alt="" />
                      </button>
                    }
                  } @else {
                    <span class="entity-thumbnail entity-thumbnail--empty" aria-label="No related photos"></span>
                  }
                </div>

                <div class="entity-card__identity">
                  <span class="entity-card__title">Drone {{ item.drone.droneId }}</span>
                  <span class="entity-card__subtitle">
                    {{ item.drone.confidence }}% confidence · {{ item.drone.observerCount }} observers
                  </span>
                  <span class="entity-card__time">
                    Last
                    {{ formatMetadataValue(item.drone, { label: 'Last seen', key: 'lastSeenAtUtc', type: 'date' }) }}
                  </span>
                </div>

                <button
                  class="entity-card__zoom"
                  type="button"
                  [attr.aria-label]="'Zoom to drone ' + item.drone.droneId"
                  [attr.title]="'Zoom to drone ' + item.drone.droneId"
                  (click)="zoomToDrone(item)"
                ></button>
              </div>

              <details class="entity-card__details">
                <summary>Metadata</summary>
                <dl>
                  @for (field of droneMetadataFields; track field.key) {
                    <div>
                      <dt>{{ field.label }}</dt>
                      <dd>{{ formatMetadataValue(item.drone, field) }}</dd>
                    </div>
                  }
                  <div>
                    <dt>Related observers</dt>
                    <dd>
                      @if (item.relatedObservers.length) {
                        @for (observer of item.relatedObservers; track observer.id) {
                          <span class="entity-inline-pill">#{{ observer.observerId }} {{ observer.deviceId }}</span>
                        }
                      } @else {
                        -
                      }
                    </dd>
                  </div>
                </dl>
              </details>
            </article>
          }
        </div>
      } @else {
        <p class="entity-section__empty">No detected drones in the current response.</p>
      }
    </section>
  }
</aside>
